var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Vue from 'vue';
import jwtDecode from 'jwt-decode';
import { showUnauthenticatedAndRedirect } from '../utils';
import getSessionContext from 'src/utils/context/getSessionContext';
import processRequestedFrontendActions from './utils/processRequestedFrontendActions';
import { getActingUser } from 'src/utils/actingUser';
import * as Sentry from '@sentry/vue';
import uuid4 from 'src/utils/uuid/UUIDv4';
import { createToast, success } from 'src/utils/notification/NotificationService';
// this function allow error catching in async/await
// https://blog.grossman.io/how-to-write-async-await-without-try-catch-blocks-in-javascript/
function to(promise) {
    return promise.then(data => {
        return [null, data];
    })
        .catch(err => [err]);
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function rpc(procedure, payload, handlerParams) {
    var _a, _b, _c, _d, _e, _f;
    return __awaiter(this, void 0, void 0, function* () {
        if (!handlerParams)
            handlerParams = null;
        const context = getSessionContext();
        if (process.env.NODE_ENV === 'prod')
            Sentry.setContext('context', context);
        // do not send RPC if required context missing
        if (payload && payload.api_version === 1 && (!context.app_id || !context.org_id || !context.tenant_id)) {
            console.warn(`rejected execution of rpc v1 call to ${payload.entity} due to missing context`);
            return;
        }
        if (!payload) {
            payload = { context };
        }
        else {
            payload = Object.assign({ context }, payload);
        }
        if (payload.action === 'select') {
            payload = Object.assign({ filter_conditions: [], sorting_model: [] }, payload);
        }
        payload.rpc_id = uuid4();
        if (process.env.DEV) {
            if (payload.api_version === 1) {
                const debugInfo = {
                    queryEquivalent: `
set sonar.user_id = ${(_a = getActingUser()) === null || _a === void 0 ? void 0 : _a.id};
set sonar.context.org_id = ${payload.context.org_id};
set sonar.context.tenant_id = ${payload.context.tenant_id};
set sonar.context.app_id = ${payload.context.app_id};
set sonar.context.as_of_date = '${payload.context.as_of_date}';
set sonar.context.scope_date_from = '${payload.context.scope_date_from}';
set sonar.context.scope_date_to = '${payload.context.scope_date_to}';
set sonar.context.scope_date_offset = ${payload.context.scope_date_offset};
set sonar.context.search_term = '${payload.context.search_term}';
select generic_controllers.rpc('${JSON.stringify(payload)}');`,
                    debugMode: true,
                    rpcDebugQuery: `select * from debugging.rpc_flow where rpc_id = '${payload.rpc_id}';`
                };
                payload = Object.assign(Object.assign({}, payload), { debugInfo });
            }
        }
        // payload = JSON.stringify(payload).replace(/'/g, "''")
        // handlerParams = JSON.stringify(handlerParams).replace(/'/g, "''")
        const endPoint = (handlerParams && handlerParams.public_action) ? 'public_action' : 'action';
        let urlHumanHint; // adding urlHumanHint serves no functional purpose. it is only to be able to see in Network tab of debugging of what's going on
        if (payload && payload.api_version === 1) {
            urlHumanHint = payload.action + '=' + payload.entity;
        }
        else {
            urlHumanHint = procedure;
        }
        // @ts-ignore
        const [err, response] = yield to(Vue.http.post(`https://api.sonar.cloud/${endPoint}?${urlHumanHint}`, { procedure, payload, handlerParams }));
        if (err) {
            if (err.status === 401) {
                try { // check for token expiration
                    const tokenHeaderIsValid = jwtDecode(localStorage.AUTH_TOKEN, { header: true });
                    const decodedJwtToken = jwtDecode(localStorage.AUTH_TOKEN);
                    const tokenExpirationInMiliseconds = decodedJwtToken.exp ? decodedJwtToken.exp * 1000 : null;
                    if (tokenHeaderIsValid && tokenExpirationInMiliseconds && tokenExpirationInMiliseconds < (new Date()).getTime()) {
                        showUnauthenticatedAndRedirect();
                    }
                    else { // token is fine, but action is not authorized
                        createToast({ message: `Your current role is not authorized to perform action ${procedure}`, type: 'error', timeout: 0, showCloseButtonOnHover: true });
                    }
                }
                catch (e) {
                    if (e.name === 'InvalidTokenError') {
                        showUnauthenticatedAndRedirect();
                    }
                }
            }
            else {
                createToast({ message: err.data.error, type: 'error', timeout: 0, showCloseButtonOnHover: true });
                return { result: 'error', messages: [err.data.error] };
            }
        }
        if (response) {
            if (((_b = response === null || response === void 0 ? void 0 : response.body) === null || _b === void 0 ? void 0 : _b.result) === 'error' && ((_c = response === null || response === void 0 ? void 0 : response.body) === null || _c === void 0 ? void 0 : _c.messages)) {
                for (const message of response.body.messages) {
                    createToast({ message, type: 'error', timeout: 0, showCloseButtonOnHover: true });
                }
            }
            if (((_d = response === null || response === void 0 ? void 0 : response.body) === null || _d === void 0 ? void 0 : _d.result) === 'success' && ((_e = response === null || response === void 0 ? void 0 : response.body) === null || _e === void 0 ? void 0 : _e.messages)) {
                for (const message of response.body.messages) {
                    success(message);
                }
            }
            if ((_f = response.body) === null || _f === void 0 ? void 0 : _f.requestedFrontendActions) {
                processRequestedFrontendActions(response.body.requestedFrontendActions);
            }
            return response.data;
        }
    });
}
export function genericControllersRpc(payload) {
    return __awaiter(this, void 0, void 0, function* () {
        return rpc('generic_controllers.rpc', payload);
    });
}
